import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity} from "./entity";
import {CustomFieldDefDataType, toCustomFieldDefDataType} from "./custom-field-def-data-type";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";

export const CustomFieldProfileItemCodec = t.intersection([
  t.type({
    id: t.string,
    customFieldProfileId: t.string,
    customFieldDefId: t.string,
    sortOrder: t.number,
    defObjectType: t.string,
    defDataType: t.string,
    defLabel: t.string,
    defRequired: t.boolean,
    defCode: t.string,
    defRefObjectType: t.string,
  }),
  t.partial({
    required: t.boolean,
    label: t.string,
    createdById: t.string,
    lastModifiedById: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export type CustomFieldProfileItem = t.TypeOf<typeof CustomFieldProfileItemCodec>;

@Exclude()
export class CustomFieldProfileItemEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  customFieldProfileId!: string;

  @Expose()
  customFieldDefId!: string;

  @Expose()
  sortOrder!: number;

  @Expose()
  defObjectType!: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toCustomFieldDefDataType))
  defDataType!: CustomFieldDefDataType;

  @Expose()
  defLabel!: string;

  @Expose()
  defRequired!: boolean;

  @Expose()
  defCode!: string;

  @Expose()
  defRefObjectType!: string;

  // ─────────────────────────────
  // Optional fields
  // ─────────────────────────────

  @Expose()
  required?: boolean;

  @Expose()
  label?: string;

  @Expose()
  createdById?: string;

  @Expose()
  lastModifiedById?: string;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  readonly entityType = "CUSTOMFIELDPROFILEITEM";

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for CustomFieldProfileItemEntity. Recommend using the {@link from} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: CustomFieldProfileItem): CustomFieldProfileItemEntity {
    return plainToInstance(CustomFieldProfileItemEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<CustomFieldProfileItem> {
    const object = instanceToPlain(this, {excludeExtraneousValues: true});
    return validateJSON(CustomFieldProfileItemCodec, object);
  }

  asTypeValue(): CustomFieldProfileItem {
    const object = instanceToPlain(this, {excludeExtraneousValues: true});
    return validateJSONNow(CustomFieldProfileItemCodec, object);
  }
}
